import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, ChevronDownIcon, Loader2Icon, PhoneIcon } from 'lucide-react';
import { PhotoUpload } from './PhotoUpload';
import { Logo } from '../Logo';
import { useLeadForm } from './useLeadForm';
import type { LeadFormValues } from './useLeadForm';
import { business, citySuggestions } from '../../data/business';
import type { EstimatePrefill, ServiceOption } from '../../types/content';
const serviceOptions: ServiceOption[] = ['Pool Resurfacing', 'New Plaster', 'Tile & Coping', 'Pavers / Deck', 'Equipment Repair', 'Full Pool Renovation', 'Not Sure Yet'];
interface LeadFormProps {
  prefill: EstimatePrefill;
}
export function LeadForm({
  prefill
}: LeadFormProps) {
  const form = useLeadForm(prefill);
  const {
    values,
    errors,
    status
  } = form;
  const fieldProps = (key: keyof LeadFormValues) => ({
    id: `lead-${key}`,
    name: key,
    value: values[key],
    'aria-invalid': Boolean(errors[key]),
    'aria-describedby': errors[key] ? `lead-${key}-error` : undefined,
    className: `field-input ${errors[key] ? '!border-danger' : ''}`
  });
  const errorText = (key: keyof LeadFormValues) => errors[key] ? <p id={`lead-${key}-error`} className="mt-1.5 text-sm font-medium text-danger" data-id="element-107" data-mp="31vje8p56hwu1">
        {errors[key]}
      </p> : null;
  return <section id="estimate" className="scroll-mt-16 bg-navy py-20 lg:py-28" aria-labelledby="estimate-title" data-id="element-108" data-mp="2x0470g1oez6l">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-12 lg:gap-16 lg:px-8" data-id="element-109" data-mp="2o5fwobh8urax">
        <div className="lg:col-span-5 lg:pt-4" data-id="element-110" data-mp="2j6cucysnez6n">
          <h2 id="estimate-title" className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white lg:text-[3.4rem]" data-id="element-111" data-mp="2j74r4cxzf2yi">
            Ready to Bring Your Pool Back to Life?
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-white/80" data-id="element-112" data-mp="297nltwmn6r32">
            Tell us about your pool and request your free renovation estimate.
          </p>

          <div className="mt-10 border-t border-white/15 pt-8" data-id="element-113" data-mp="1ukynhy44mjy9">
            <p className="text-[15px] text-white/75" data-id="element-114" data-mp="1unt8pruys8sq">After you send your request, a member of our team will reach out to talk about your pool and schedule a time to see it.</p>
            <p className="mt-8 text-sm font-semibold text-accent" data-id="element-115" data-mp="1lmmob7p72du7">Prefer to talk it through?</p>
            <a href={business.phoneHref} className="mt-2 inline-flex items-center gap-3 font-display text-2xl font-bold text-white transition-colors duration-150 hover:text-accent" data-id="element-116" data-mp="1qkeosq3wn8a0">
              <PhoneIcon className="h-6 w-6" aria-hidden="true" data-id="element-117" data-mp="2icvo0t24lpk2" />
              {business.phoneDisplay}
            </a>
            <p className="mt-2 text-sm text-white/60" data-id="element-118" data-mp="29ih9ispf6j20">Serving Orlando, Kissimmee, Winter Garden, Clermont &amp; nearby.</p>
          </div>
        </div>

        <div className="lg:col-span-7" data-id="element-119" data-mp="2x6qsznz5uupz">
          <div className="rounded-3xl bg-white p-6 shadow-2xl sm:p-8 lg:p-10" data-id="element-120" data-mp="31z1qylacc6fw">
            <AnimatePresence mode="wait" initial={false} data-id="element-121" data-mp="3214nlo600xtl">
              {status === 'success' ? <motion.div key="success" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} className="py-10 text-center" role="status" data-id="element-122" data-mp="3bbhajhtegg2u">
                  <div className="mb-8 flex justify-center" data-id="element-123" data-mp="2sfhqct6nygtt">
                    <Logo className="h-14" data-id="element-124" data-mp="2odw5akjz6w8j" />
                  </div>
                  <CheckCircle2Icon className="mx-auto h-14 w-14 text-aqua" aria-hidden="true" data-id="element-125" data-mp="2ofquib2a5aif" />
                  <h3 className="mt-5 font-display text-3xl font-extrabold text-navy" data-id="element-126" data-mp="32asxapbtyjr0">
                    Thanks, {values.name.trim().split(' ')[0]}. Your request is in.
                  </h3>
                  <p className="mx-auto mt-3 max-w-md text-lg text-navy/70" data-id="element-127" data-mp="hp116wtslnho">
                    We'll contact you at {values.phone} to talk about your pool and set up your free estimate.
                  </p>
                  <button type="button" onClick={form.reset} className="btn-outline mt-8" data-id="element-128" data-mp="2nb76r1744qkr">
                    Send another request
                  </button>
                </motion.div> : <motion.form key="form" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.18
            }} onSubmit={form.submit} noValidate className="grid gap-5 sm:grid-cols-2" data-id="element-129" data-mp="2wbwwbn5s5kq1">
                  <div className="sm:col-span-2" data-id="element-130" data-mp="3b2w1spbbjt5e">
                    <label htmlFor="lead-name" className="field-label" data-id="element-131" data-mp="3fyun241cov1l">Name</label>
                    <input {...fieldProps('name')} type="text" autoComplete="name" placeholder="Your full name" onChange={e => form.setField('name', e.target.value)} data-id="element-132" data-mp="3k1zn7zxykkn2" />
                    {errorText('name')}
                  </div>
                  <div data-id="element-133" data-mp="1de0h9ormq5qc">
                    <label htmlFor="lead-phone" className="field-label" data-id="element-134" data-mp="1db4hduw816z9">Phone</label>
                    <input {...fieldProps('phone')} type="tel" inputMode="tel" autoComplete="tel" placeholder="(407) 000-0000" onChange={e => form.setField('phone', e.target.value)} data-id="element-135" data-mp="18ct5zfqa2oex" />
                    {errorText('phone')}
                  </div>
                  <div data-id="element-136" data-mp="1w48c7jl6dpvg">
                    <label htmlFor="lead-email" className="field-label" data-id="element-137" data-mp="1w2ni1v2rw8y4">Email</label>
                    <input {...fieldProps('email')} type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" onChange={e => form.setField('email', e.target.value)} data-id="element-138" data-mp="1s1v6gv6fhryo" />
                    {errorText('email')}
                  </div>
                  <div data-id="element-139" data-mp="1iahdl68zfqei">
                    <label htmlFor="lead-city" className="field-label" data-id="element-140" data-mp="2frfuhshfvfet">City</label>
                    <input {...fieldProps('city')} type="text" list="lead-city-options" autoComplete="address-level2" placeholder="Orlando" onChange={e => form.setField('city', e.target.value)} data-id="element-141" data-mp="2b0zf42u25jpt" />
                    <datalist id="lead-city-options" data-id="element-142" data-mp="2ywb66f5idu0d">
                      {citySuggestions.map(c => <option key={c} value={c} data-id="element-143" data-mp="2l561uzr74djp" />)}
                    </datalist>
                    {errorText('city')}
                  </div>
                  <div data-id="element-144" data-mp="2p4euu2eew7e7">
                    <label htmlFor="lead-zip" className="field-label" data-id="element-145" data-mp="2b6qacvo9b29o">ZIP Code</label>
                    <input {...fieldProps('zip')} type="text" inputMode="numeric" autoComplete="postal-code" maxLength={5} placeholder="32801" onChange={e => form.setField('zip', e.target.value.replace(/\D/g, ''))} data-id="element-146" data-mp="2b931nzfwmoze" />
                    {errorText('zip')}
                  </div>
                  <div className="sm:col-span-2" data-id="element-147" data-mp="267x5tsqy6lln">
                    <label htmlFor="lead-service" className="field-label" data-id="element-148" data-mp="e5niz42y65p">Service Needed</label>
                    <div className="relative" data-id="element-149" data-mp="4gsfr14nxt3f">
                      <select {...fieldProps('service')} onChange={e => form.setField('service', e.target.value as ServiceOption | '')} className={`${fieldProps('service').className} appearance-none pr-12 ${values.service ? '' : 'text-navy/40'}`} data-id="element-150" data-mp="4fhdj5qv8tef">
                        <option value="" disabled data-id="element-151" data-mp="1tww4bkjn094c">Choose a service</option>
                        {serviceOptions.map(s => <option key={s} value={s} className="text-navy" data-id="element-152" data-mp="28kzbuydbvsv9">{s}</option>)}
                      </select>
                      <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-navy/60" aria-hidden="true" data-id="element-153" data-mp="23pjye7j8fyzd" />
                    </div>
                    {errorText('service')}
                  </div>
                  <div className="sm:col-span-2" data-id="element-154" data-mp="na0lbeu12eek">
                    <label htmlFor="lead-description" className="field-label" data-id="element-155" data-mp="nasi2szd2i6f">
                      Tell us about your pool <span className="font-normal text-navy/50" data-id="element-156" data-mp="ic8rugdqik89">(optional)</span>
                    </label>
                    <textarea {...fieldProps('description')} rows={4} placeholder="Age of the pool, what you're noticing, and what you'd like to change" onChange={e => form.setField('description', e.target.value)} className={`${fieldProps('description').className} resize-y`} data-id="element-157" data-mp="1fs5cj37nvsv" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-158" data-mp="3end6kru0ois5">
                    <PhotoUpload photos={form.photos} max={form.maxPhotos} onAdd={form.addPhotos} onRemove={form.removePhoto} data-id="element-159" data-mp="3eo50zzbvoagu" />
                  </div>
                  <div className="sm:col-span-2" data-id="element-160" data-mp="3olj46x8sj2r7">
                    {form.submitError && <p role="alert" className="mb-4 rounded-xl bg-danger/10 px-4 py-3 text-sm font-medium text-danger" data-id="element-161" data-mp="yu4gnb8c7fdl">
                        {form.submitError}
                      </p>}
                    <button type="submit" disabled={status === 'submitting'} className="btn-primary w-full py-5 text-lg disabled:cursor-wait disabled:opacity-80" data-id="element-162" data-mp="2ob01l7oqfx5u">
                      {status === 'submitting' ? <>
                          <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-163" data-mp="29neyhdruxxxx" />
                          Sending your request…
                        </> : 'Get My Free Estimate'}
                    </button>
                    <p className="mt-3 text-center text-sm text-navy/55" data-id="element-164" data-mp="20p391f2q9672">Free, no-obligation estimate. We never share your information.</p>
                  </div>
                </motion.form>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}